// Styles
import { createPinia } from 'pinia'
// Vue & Plugins
import { createApp } from 'vue'

import { QueryClient, VueQueryPlugin } from '@tanstack/vue-query'
import VueDatePicker from '@vuepic/vue-datepicker'
import { Ray } from 'node-ray/dist/web'
import 'primeicons/primeicons.css'
import 'shepherd.js/dist/css/shepherd.css'
// import './tokens.css'
import { DataLoaderPlugin } from 'vue-router/experimental'

// import { I18nOptions, createI18n } from 'vue-i18n'

// Components
import App from './App.vue'
import PrimaryButton from './common/components/buttons/PrimaryButton.vue'
import RefreshButton from './common/components/buttons/RefreshButton.vue'
import SecondaryButton from './common/components/buttons/SecondaryButton.vue'
import TweenNumber from './common/components/fields/TweenNumber.vue'
import FormTextInput from './common/components/inputs/FormTextInput.vue'
import FormTextInputFloat from './common/components/inputs/FormTextInputFloat.vue'
import AppLayout from './common/components/layouts/AppLayout.vue'
import GuestLayout from './common/components/layouts/GuestLayout.vue'
// Sentry
// import * as Sentry from '@sentry/vue'
import { Tooltip } from 'floating-vue'

import { useAuthUserStore } from './user/authUserStore'

import { setupInterceptors } from './axiosInstance'
import { createAppI18n } from './i18n.ts'
import { installOnboardingGuard } from './onboarding/guard.ts'
// Other
// import enUS from './common/locales/en-US.json'
import { registerPrimeVue } from './primeVue'
import router from './router'
import { IdGenKey } from './shared/di/keys.ts'
import '@fontsource/barlow/400.css'
import '@fontsource/barlow/500.css'
import '@fontsource/barlow/600.css'
import './style.css'

//import * as Spotlight from '@spotlightjs/spotlight'

Ray.useDefaultSettings({ enable: false })

const app = createApp(App)

// Components
app.component('AppLayout', AppLayout)
app.component('GuestLayout', GuestLayout)
app.component('VueDatePicker', VueDatePicker)
app.component('FormTextInput', FormTextInput)
app.component('FormTextInputFloat', FormTextInputFloat)
app.component('PrimaryButton', PrimaryButton)
app.component('RefreshButton', RefreshButton)
app.component('SecondaryButton', SecondaryButton)
app.component('TweenNumber', TweenNumber)
app.component('VTooltip', Tooltip)
// Plugins
// Pinia
const pinia = createPinia()
app.use(pinia)

// VueQuery
function createAppQueryClient() {
  return new QueryClient({
    defaultOptions: {
      queries: {
        retry: false,
      },
    },
  })
}
const queryClient = createAppQueryClient()
// const vueQueryPluginOptions: VueQueryPluginOptions = {
//   queryClientConfig: {
//     defaultOptions: { queries: { retry: false } },
//   },
// }
app.use(VueQueryPlugin, { queryClient })

// Router
app.use(DataLoaderPlugin, { router })
installOnboardingGuard(router, queryClient)
app.use(router)

// PrimeVue
registerPrimeVue(app)

// i18n
// const options: I18nOptions = {
//   legacy: false,
//   missingWarn: false,
//   fallbackWarn: false,
//   locale: 'en-US',
//   fallbackLocale: 'en-US',
//   messages: {
//     'en-US': enUS,
//   },
//   numberFormats: {
//     'en-US': {
//       currency: {
//         style: 'currency',
//         currencyDisplay: 'symbol',
//         currency: 'USD',
//       },

//       decimal: {
//         style: 'decimal',
//         minimumFractionDigits: 0,
//         maximumFractionDigits: 2,
//       },
//     },
//   },
// }

// export function createAppI18n() {
//   return createI18n<false, typeof options>(options)
// }

// export function createTranslator() {
//   return createAppI18n().global.t
// }

const i18n = createAppI18n()
// const i18n = createI18n<false, typeof options>(options)
app.use(i18n)

const authUserStore = useAuthUserStore()

setupInterceptors({
  onUnauthorized: ({ skipAuthRedirect }) => {
    if (skipAuthRedirect) {
      return
    }

    if (authUserStore.user !== null) {
      authUserStore.setSessionExpired()
    }

    authUserStore.user = null

    router.push({ name: '/(guest)/login' })
  },
  onPasswordExpired: () => {
    router.push({
      name: '/(user)/user/password',
      hash: '#password-expired',
    })

    authUserStore.setPasswordExpired()
  },
})

// Sentry
// Sentry.init({
//   app: app,
//   dsn: import.meta.env.VITE_SENTRY_DSN,
//   // Set tracesSampleRate to 1.0 to capture 100%
//   // of transactions for performance monitoring.
//   // We recommend adjusting this value in production
//   tracesSampleRate: 0.1,
//   logErrors: true, // Allow vue to call logError internally, showing errors in the developer console // TODO: Review if this functionality is desirable in production
// })

if (import.meta.env.DEV) {
  // Spotlight.init()
}

// DI
app.provide(IdGenKey, () => crypto.randomUUID())

app.mount('#app')
